import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { C, F } from '../landingTheme';
import { PLAN_COPY, inr } from './pricingCopy';
import { trackCta } from '../../../utils/marketingTracker';

const PERIOD = { 12: 'per year', 6: 'per half-year', 1: 'per month' };

// Effective price for the selected cycle (founding replaces the annual discount while spots remain).
export const priceFor = (plan, cycle, months, founding) => {
  const q = plan.cycles?.[cycle] || {};
  const fa = cycle === 'annual' && founding?.available ? plan.founding_annual : null;
  const eff = fa || q;
  return {
    monthly: eff.monthly_equivalent, total: eff.total, pct: eff.discount_percent || 0,
    founding: !!fa, regularTotal: plan.monthly_price * months, period: PERIOD[months] || `per ${months} months`,
  };
};

export function PlanCard({ plan, cycle, months, founding }) {
  const copy = PLAN_COPY[plan.code] || {};
  const hot = plan.code === 'chain';
  const p = priceFor(plan, cycle, months, founding);
  const locs = plan.included_locations;
  const scope = `${plan.code === 'chain' ? 'Up to ' : ''}${locs} clinic location${locs === 1 ? '' : 's'} · ${plan.included_users} users`;
  return (
    <div data-testid={`pricing-card-${plan.code}`}
      className="relative rounded-3xl border bg-white p-7 sm:p-8 flex flex-col transition-transform duration-300 hover:-translate-y-1"
      style={{ borderColor: hot ? C.saffron : C.border, boxShadow: hot ? `0 30px 60px -32px ${C.saffron}` : '0 1px 0 rgba(0,0,0,0.03)' }}>
      {hot && (
        <span className="absolute -top-3 left-7 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white"
          style={{ background: C.saffron, fontFamily: F.mono }} data-testid="pricing-popular-badge">Most Popular</span>
      )}
      <p className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ fontFamily: F.mono, color: hot ? C.saffron : C.ink2 }}>{plan.name}</p>
      <p className="mt-1 text-sm" style={{ fontFamily: F.body, color: C.ink2 }}>{copy.tagline}</p>

      <div className="mt-6 h-6 flex items-center gap-2 text-sm" style={{ fontFamily: F.body }}>
        {p.pct > 0 && (
          <>
            <s style={{ color: '#9A9CA5' }} data-testid={`pricing-strike-${plan.code}`}>{inr(plan.monthly_price)}/month</s>
            <span className="rounded px-1.5 py-0.5 text-[11px] font-semibold" data-testid={`pricing-save-${plan.code}`}
              style={{ background: p.founding ? C.saffronTint : C.emeraldTint, color: p.founding ? C.saffron : C.emerald }}>
              {p.founding ? `Founding ${p.pct}% off` : `Save ${p.pct}%`}
            </span>
          </>
        )}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5 whitespace-nowrap">
        <span className="text-5xl font-extrabold" style={{ fontFamily: F.display, color: C.ink, letterSpacing: '-0.04em' }} data-testid={`pricing-price-${plan.code}`}>
          {inr(p.monthly)}
        </span>
        <span className="text-sm" style={{ fontFamily: F.body, color: C.ink2 }}>/month + GST</span>
      </div>
      <div className="mt-2 text-[13px]" style={{ fontFamily: F.body, color: C.ink2 }} data-testid={`pricing-billed-${plan.code}`}>
        {months > 1
          ? <>Billed <b style={{ color: C.ink }}>{inr(p.total)} + GST</b> {p.period}{p.pct > 0 && <> <s className="opacity-60">{inr(p.regularTotal)}</s></>}</>
          : 'Billed monthly'}
      </div>

      <p className="mt-5 pt-5 border-t text-sm font-semibold" style={{ borderColor: C.border, fontFamily: F.body, color: C.ink }} data-testid={`pricing-scope-${plan.code}`}>{scope}</p>
      <FeatureList intro={copy.intro} features={copy.features} />
      <Link to={`/signup?plan=${plan.code}&cycle=${cycle}`} data-testid={`pricing-cta-${plan.code}`}
        onClick={() => trackCta('signup_cta', { plan: plan.code, cycle })}
        className="mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-colors"
        style={hot ? { background: C.saffron, color: 'white', fontFamily: F.display } : { background: C.ink, color: 'white', fontFamily: F.display }}>
        {copy.cta} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
      </Link>
    </div>
  );
}

export function EnterpriseCard({ minLocations, onTalk }) {
  const copy = PLAN_COPY.enterprise;
  return (
    <div data-testid="pricing-card-enterprise" className="rounded-3xl border p-7 sm:p-8 flex flex-col text-white transition-transform duration-300 hover:-translate-y-1"
      style={{ background: C.navy, borderColor: C.navySurface }}>
      <p className="text-[11px] uppercase tracking-[0.2em] font-semibold opacity-70" style={{ fontFamily: F.mono }}>{copy.name}</p>
      <p className="mt-1 text-sm opacity-70" style={{ fontFamily: F.body }}>{copy.tagline}</p>
      <div className="mt-6 h-6" />
      <p className="mt-1 text-5xl font-extrabold" style={{ fontFamily: F.display, letterSpacing: '-0.04em' }} data-testid="pricing-price-enterprise">Custom</p>
      <p className="mt-2 text-[13px] opacity-70" style={{ fontFamily: F.body }}>Custom pricing for your network</p>
      <p className="mt-5 pt-5 border-t border-white/15 text-sm font-semibold" style={{ fontFamily: F.body }} data-testid="pricing-scope-enterprise">{minLocations}+ locations · custom users</p>
      <FeatureList intro={copy.intro} features={copy.features} dark />
      <button onClick={() => { trackCta('contact_cta', { plan: 'enterprise' }); onTalk(); }} data-testid="pricing-cta-enterprise"
        className="mt-8 inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-6 py-3.5 font-semibold hover:bg-white/10 transition-colors"
        style={{ fontFamily: F.display }}>
        {copy.cta} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
      </button>
    </div>
  );
}

function FeatureList({ intro, features, dark }) {
  return (
    <div className="mt-4 flex-1">
      <p className="text-[12px] font-semibold uppercase tracking-wider" style={{ fontFamily: F.mono, color: dark ? 'rgba(255,255,255,0.6)' : C.ink2 }}>{intro}</p>
      <ul className="mt-3 space-y-2">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm" style={{ fontFamily: F.body, color: dark ? 'white' : C.ink }}>
            <Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5} style={{ color: dark ? C.saffron : C.emerald }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
